import React, {FC} from 'react';
import {connect} from 'react-redux';

import {MessageTypesEnum} from '../../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {LinkedConversationStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {LoadedMessageModel} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {ReplyLocationsEnum} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {getHasOmniChannelLinkedConversations} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {ResolveStateProps} from '../../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  AccountDetailsPanelId,
  ContactDetailsPanelId,
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {
  makeGetCanSplitMessage,
  makeGetShowReplyAction,
} from '../../../../../../srcCommon/inboxes/viewer/activity/message/messageViewerSelectors';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {LegacyMessageMoreDropdownEntryPointModel} from '../../../../../models/api/legacyEntryPointModel';
import {AppState} from '../../../../../store/appState';
import {getLegacyMessageMoreDropdownEntryPoints} from '../../../../../store/data/applications/legacyApplicationsSelectors';
import {getCompanyHost} from '../../../../../store/data/company/companySelectors';
import {getHasLinkedConversations} from '../../../../../store/data/conversations/linkedConversationsSelectors';
import {toggleMarkConversationAsSeenAction} from '../../../../../store/data/conversations/seenConversationsActions';
import {EmlSource} from '../../../../../store/panels/eml/emlPanelState';
import {popoutMessageSourceAsyncAction} from '../../../../../store/panels/messageSource/messageSourcePanelActions';
import {useMessagePrintHandler} from '../../../../popups/popupPrintHelpers';
import {CloneHandler, ForwardHandler, MessageViewerMoreDropdown} from './messageViewerMoreDropdown';

/*
 * Props.
 */

interface MessageViewerMoreDropdownContainerPublicProps {
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId | AccountDetailsPanelId | ContactDetailsPanelId;
  conversation: ConversationModel | undefined;
  message: LoadedMessageModel;
  isFirstMessage?: boolean;
  cellsContext: CellsContext;
  selectedInboxId: number | undefined;
  emlSource?: EmlSource;
}

interface MessageViewerMoreDropdownContainerComponentProps
  extends MessageViewerMoreDropdownContainerPublicProps,
    PopoverTransferProps,
    ResolveStateProps<typeof mapStateToProps>,
    ResolveDispatchProps<typeof mapDispatchToProps> {
  namespaces: string[];
  isInTeammateContext: boolean;
  onReplyClick: (arg: ReplyLocationsEnum) => void;
  onReplyAllClick: (arg: ReplyLocationsEnum) => void;
  onForwardClick: ForwardHandler;
  onCloneClick: CloneHandler;
  onSaveEmailClick: () => void;
  onPopoutClick: () => void;
  onSplitClick: () => void;
  onDestroyClick: () => void;
  onEntryPointSelect: (model: LegacyMessageMoreDropdownEntryPointModel) => void;
  onPopoutLinkedConversationClick: () => void;
}

/*
 * Component.
 */

const MessageViewerMoreDropdownContainerComponent: FC<MessageViewerMoreDropdownContainerComponentProps> = (
  props,
) => {
  const {panelId, conversation, cellsContext, message, onPopoutLinkedConversationClick} = props;

  const onPrintClick = useMessagePrintHandler(message, props.emlSource);

  const onViewSourceClick = () => {
    props.popoutMessageSourceAsync(panelId, message);
  };

  const onMarkUnreadClick = () => {
    if (!conversation) {
      return;
    }

    props.toggleMarkConversationAsSeen([conversation.id], false, {
      cellsContext,
      seenAt: props.isFirstMessage ? 0 : message.sentAt - 1000,
    });
  };

  return (
    <MessageViewerMoreDropdown
      {...props}
      onPrintClick={onPrintClick}
      onViewSourceClick={onViewSourceClick}
      onMarkUnreadClick={onMarkUnreadClick}
      onPopoutLinkedConversationClick={onPopoutLinkedConversationClick}
      openInBrowser={openInBrowser}
    />
  );
};

/*
 * Selectors.
 */

function getConversation(_state: AppState, props: MessageViewerMoreDropdownContainerPublicProps) {
  return props.conversation;
}

function getMessage(_state: AppState, props: MessageViewerMoreDropdownContainerPublicProps) {
  return props.message;
}

function getSelectedInboxId(_state: AppState, props: MessageViewerMoreDropdownContainerPublicProps) {
  return props.selectedInboxId;
}

function makeGetCanLinkConversation() {
  return createSelector(
    [getHasLinkedConversations, getConversation, getHasOmniChannelLinkedConversations],
    (hasLinkedConversations, conversation, hasOmniChannelLinkedConversations) => {
      const canLinkConversation =
        hasLinkedConversations &&
        conversation &&
        conversation.linkStatus !== LinkedConversationStatusesEnum.LINKED;
      if (!hasOmniChannelLinkedConversations) {
        return Boolean(canLinkConversation && conversation.messageType === MessageTypesEnum.EMAIL);
      }

      return Boolean(canLinkConversation);
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<MessageViewerMoreDropdownContainerPublicProps>()(() => ({
  host: getCompanyHost,
  canSplit: makeGetCanSplitMessage(getConversation, getMessage),
  showReplyAction: makeGetShowReplyAction(getMessage, getSelectedInboxId),
  entryPoints: getLegacyMessageMoreDropdownEntryPoints,
  canLinkConversation: makeGetCanLinkConversation(),
  hasOmniChannelLinkedConversations: getHasOmniChannelLinkedConversations,
}));

const mapDispatchToProps = makeMapDispatchToProps<MessageViewerMoreDropdownContainerPublicProps>()(() => ({
  popoutMessageSourceAsync: popoutMessageSourceAsyncAction,
  toggleMarkConversationAsSeen: toggleMarkConversationAsSeenAction,
}));

export const MessageViewerMoreDropdownContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MessageViewerMoreDropdownContainerComponent);
